// In-app AI quiz generation against the Gemini API (Google AI Studio free
// tier). Host-only surface (CreateQuiz is behind the presenter/admin route),
// and the API key is BYO: each host pastes their own free AI Studio key, held
// ONLY in this browser's localStorage — never in the bundle, never in RTDB —
// so a leaked build can't burn anyone's quota and every presenter brings
// their own daily allowance.
//
// Free-tier budgets (per key, July 2026): the text chain gives ~540 quizzes/day
// (20 + 20 + 500 RPD), the grounded chain 40/day (20 + 20). One generation =
// one request, so RPM caps (5, lite 15) never bite. On quota/availability
// errors we fall through to the next model; hard errors (bad key) surface.

export interface GenerateQuizOpts {
  topic: string;
  /** Optional pasted source material — questions are drawn from it when set. */
  content?: string;
  count: number;
  /** Ground on Google Search (2.5 Flash chain — only those support the tool on free tier). */
  grounded?: boolean;
  /** One or more AI Studio keys. Quota is per key, so a pool multiplies the daily allowance. */
  apiKeys: string[];
}

export interface GeneratedQuestion {
  questionText: string;
  options: { optionText: string; isCorrect: boolean }[];
  timer: number;
}

// Newest/smartest first; every fallback is strictly cheaper-quota than the
// one before it, so trying in order can only help.
const TEXT_CHAIN = ['gemini-3.5-flash', 'gemini-3-flash-preview', 'gemini-3.1-flash-lite'];
const GROUNDED_CHAIN = ['gemini-2.5-flash', 'gemini-2.5-flash-lite'];

const KEY_STORAGE = 'quizlive.gemini-keys';
export const getSavedGeminiKeys = (): string => {
  try { return localStorage.getItem(KEY_STORAGE) ?? ''; } catch { return ''; }
};
export const saveGeminiKeys = (keys: string) => {
  try {
    if (keys.trim()) localStorage.setItem(KEY_STORAGE, keys);
    else localStorage.removeItem(KEY_STORAGE);
  } catch { /* private mode — keys just won't persist */ }
};
/** Split a pasted blob (newlines/spaces/commas) into individual keys. */
export const parseKeys = (blob: string): string[] =>
  blob.split(/[\s,]+/).map((k) => k.trim()).filter(Boolean);

// Gemini's structured-output schema (OpenAPI subset). Grounded calls can't use
// it (search tool + responseSchema don't combine), so they get prompt-enforced
// JSON instead and the lenient parse below.
const RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    questions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          questionText: { type: 'string' },
          options: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                optionText: { type: 'string' },
                isCorrect: { type: 'boolean' },
              },
              required: ['optionText', 'isCorrect'],
            },
          },
          timer: { type: 'integer' },
        },
        required: ['questionText', 'options', 'timer'],
      },
    },
  },
  required: ['questions'],
} as const;

const buildPrompt = ({ topic, content, count, grounded }: GenerateQuizOpts): string => {
  const parts = [
    `You are writing a live, Kahoot-style trivia quiz${topic.trim() ? ` about "${topic.trim()}"` : ''}. Generate exactly ${count} questions.`,
  ];
  if (content?.trim()) {
    parts.push(`Base every question ONLY on this source material:\n"""\n${content.trim()}\n"""`);
  }
  if (grounded) {
    parts.push('Use Google Search to verify every fact — questions must be accurate and current.');
  }
  parts.push(`Return ONLY valid JSON — no markdown fences, no commentary — in EXACTLY this shape:
{"questions":[{"questionText":"the question","options":[{"optionText":"an answer","isCorrect":true},{"optionText":"another","isCorrect":false}],"timer":20}]}

Rules:
- 2 to 4 options per question.
- Exactly ONE option per question has "isCorrect": true; all others false.
- "timer" is seconds to answer (use 15-30; harder = longer).
- Keep questions punchy, factual, and unambiguous — they are read aloud on a live stream.
- Vary difficulty from warm-up to hard.`);
  return parts.join('\n\n');
};

const extractJson = (text: string): any => {
  const stripped = text.replace(/```(?:json)?/gi, '').trim();
  const start = stripped.indexOf('{');
  const end = stripped.lastIndexOf('}');
  if (start === -1 || end <= start) throw new Error('no JSON found');
  return JSON.parse(stripped.slice(start, end + 1));
};

const callModel = async (model: string, apiKey: string, opts: GenerateQuizOpts): Promise<GeneratedQuestion[]> => {
  const grounded = !!opts.grounded;
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': apiKey, // header, not URL — keys don't belong in query strings/logs
      },
      body: JSON.stringify({
        contents: [{ parts: [{ text: buildPrompt(opts) }] }],
        ...(grounded ? { tools: [{ google_search: {} }] } : {}),
        generationConfig: grounded
          ? { temperature: 0.7 }
          : { temperature: 0.8, responseMimeType: 'application/json', responseSchema: RESPONSE_SCHEMA },
      }),
    }
  );

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try { message = (await res.json())?.error?.message ?? message; } catch { /* keep status */ }
    const err = new Error(message) as Error & { status: number };
    err.status = res.status;
    throw err;
  }

  const data = await res.json();
  const text: string = (data?.candidates?.[0]?.content?.parts ?? [])
    .map((p: any) => p?.text ?? '')
    .join('');
  if (!text) throw Object.assign(new Error('empty response'), { status: 500 });

  const parsed = grounded ? extractJson(text) : JSON.parse(text);
  const questions = parsed?.questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    throw Object.assign(new Error('response had no questions'), { status: 500 });
  }
  return questions;
};

export const generateQuiz = async (
  opts: GenerateQuizOpts
): Promise<{ questions: GeneratedQuestion[]; model: string }> => {
  const chain = opts.grounded ? GROUNDED_CHAIN : TEXT_CHAIN;
  const keys = opts.apiKeys.filter(Boolean);
  if (keys.length === 0) throw new Error('No API key — paste at least one AI Studio key.');

  // Quota is per (key, model): exhaust every key on the best model before
  // stepping down the chain, so a key pool keeps quality high all day.
  let lastError: any;
  for (const model of chain) {
    for (const key of keys) {
      try {
        return { questions: await callModel(model, key, opts), model };
      } catch (e: any) {
        // Dead key, quota, overload, or a parse hiccup — the next key/model
        // may still succeed; the loops are small so just keep going.
        lastError = e;
      }
    }
  }
  if (lastError?.status === 400 || lastError?.status === 403) {
    throw new Error('Gemini rejected the API key(s) — check them at aistudio.google.com/apikey.');
  }
  if (lastError?.status === 429) {
    throw new Error(
      "Today's free Gemini quota is used up on every key — try again tomorrow, or untick web grounding (bigger daily allowance)."
    );
  }
  throw new Error(`Generation failed: ${lastError?.message ?? 'all models unavailable'}`);
};
